import { Component, ViewChild, ViewEncapsulation } from "@angular/core";
import { CreateEditUserDialog, User, users_translations } from "@task-modules/users";
import { TaskRoutes } from "@task-utils/routes";
import { Task } from "@task-utils/types";
import { Templates } from "@task-utils/utils";
import { color, ConfirmDialogComponent, DataTableComponent, Frame, GenericFilterComponent, language, modal, TableConfig, template, CurvyComponentsModule, GenericFilter } from "curvy";
import { CreateEditExternalWorkerDialog } from "./create-edit-external-worker.dialog";
import { jobs_management_translations } from "../jobs.trans";
import { TaskFilterComponent } from "@task-components/single-row-filter/single-row-filter.component";

@Component({
   selector: 'task-external-workers',
   template: `
      <div class="temp-add-button" *ngIf="can_create">
            <button (click)="add_user()"
                class="filled pill"
                color="primary"
            >
                <i icon="add_circle"></i>
                <span>{{ 'USER.ADD_NEW' | trans }}</span>
            </button>
        </div>

      <knw-data-table #externalWorkersTable [config]="tableConfig"></knw-data-table>
   
   `,
   encapsulation: ViewEncapsulation.None,
   styles: [`
      task-external-workers {
         display: flex;
         flex-flow: column nowrap;
         overflow: hidden !important;
      }
   `],
})
export class ExternalWorkersComponent {
   @ViewChild('externalWorkersTable') table: DataTableComponent<Task.User, DisplayUser, 'external_workers', TaskFilterComponent>;
   can_create = false;

   constructor() {
      Frame.set({
         title: "NAVROUTE.EXTERNAL_WORKERS",
         visible: true,
         layout: "middle",
         size: "full"
      });

      language.load_translations(jobs_management_translations);
      language.load_translations(users_translations);

      User.currentTypeIs(Task.USER_TYPE.HR_MANAGER, Task.USER_TYPE.ADMIN, Task.USER_TYPE.SYSTEM_ADMIN).then(res => {
         this.can_create = res;
      })
   }

   add_user() {
      modal.open(CreateEditExternalWorkerDialog, (res) => {
         if (res) {
            this.table.getData();
         }
      })
   }

   get_filters = async () => {

      let filters: TaskFilterComponent["filterInput"] = [
         GenericFilter.text("SEARCH", "first_name+ +last_name"),
         GenericFilter.dropdown("USER.WORKER_TYPE", "is_student",[
            { label: "STUDENT", value: true },
            { label: "USER.TYPE.EXTERNAL_WORKER", value: false },
         ]),
         GenericFilter.text("INSTITUTION", "institution"),
         GenericFilter.dropdown("STATUS", "status_id", [
            { label: "STATUS.ACTIVE", value: Task.STATUS.CREATED},
            { label: "STATUS.INACTIVE", value: Task.STATUS.INACTIVE },
         ])
      ];

      return filters;
   }

   tableConfig: TableConfig<Task.User, DisplayUser, 'external_workers', GenericFilterComponent> = {
      tableName: 'external_workers',
      getData: async (filter, paginator, sort_header) => {
         let parsed_filter = { search: [], attributes: [], sort: null };
         for (let key in filter) {
            parsed_filter.attributes.push(key);
            parsed_filter.search.push(filter[key]);
         }

         // show only external workers
         parsed_filter.attributes.push('#=:user_type_id');
         parsed_filter.search.push(Task.USER_TYPE.EXTERNAL_WORKER);

         if (sort_header) {
            switch (sort_header.label) {
               case 'name':
                  sort_header.label = "last_name";
                  break;
               case 'is_student':
                  sort_header.label = "#:is_student";
                  break;
               case 'institution':
                  sort_header.label = "institution";
                  break;
               case 'contact':
                  sort_header.label = "contact_phone";
                  break;
            }
            parsed_filter.sort = sort_header;
         }

         return await TaskRoutes.users.api_user_search(parsed_filter, paginator);
         
      },
      getHeader: (header) => {
         switch (header) {
            case 'status':
               return { icon: "offline_bolt", label: "", sortable: false };
            case 'name':
               return { icon: "person", label: "NAME", sortable: true };
            case 'is_student':
               return { icon: "check", label: "STUDENT", sortable: true };
            case 'institution':
               return { icon: "school", label: "INSTITUTION", sortable: true };
            case 'contact':
               return { icon: "email", label: "CONTACT", sortable: false };
          
         }
      },
      unwrapData: (user) => {
         return {
            status: template.transform(`
               <div class="flex-column align-center justify-center">
                  <i style="color: {{icon_color}}">{{icon_name|icon}}</i>
                  <div style="font-size: 0.8em">
                     {{ status_name | trans }}
                  </div>
               </div>
            `, {
               icon_name: Task.StatusIcons[user.status_id].icon,
               icon_color: Task.StatusIcons[user.status_id].color,
               status_name: 
                  user.status_id == Task.STATUS.CREATED
                     ? language.translate("STATUS.ACTIVE")
                     : language.translate("STATUS."+Task.Status[user.status_id]),
            }),
            name: Templates.user(user),
            is_student: template.transform(`
               <div class="flex-column">
                  <div class="flex-row align-center justify-center">
                     
                     <div style="white-space: nowrap" >{{ student | icon }}</div>
                  </div>
               </div>
            `,{
               student: user.is_student ? "check" : "clear",
            }),
            institution: template.transform(`
               <div class="flex-column">
                  <div class="flex-row align-center justify-center">
                     <div style="white-space: nowrap">{{ institution }}</div>
                  </div>
               </div>
            `,{
               institution: user.institution || "",
            }),
            contact: template.transform(`
               <div class="flex-column">
                  <div class="flex-row align-center" style="gap: 0.5em;">
                     <i icon="phone"></i>
                     <div style="white-space: nowrap">{{phone}}</div>
                  </div>
                  <div class="flex-row align-center" style="gap: 0.5em;">
                     <i icon="email"></i>
                     <div style="white-space: nowrap">{{email}}</div>
                  </div>
               </div>
            `, {
            
               phone: user.contact_phone || "-",
               email: user.email || "-",
            })
         };
      },
      filterComponent: TaskFilterComponent,
      filterInput: this.get_filters,
      maxActions: window.innerWidth < 600 ? 1 : 3,
      rowActions: [
         {
            label: language.translate('EDIT'),
            icon: "edit",
            onClick: (usr) => {
                modal.open(CreateEditExternalWorkerDialog, (res) => {
                    if (res) {
                        this.table.getData();
                    }
                }, usr);
            },
            priority: 1,
            isVisible: async (user) => await User.currentTypeIs(
               Task.USER_TYPE.HR_MANAGER,
               Task.USER_TYPE.ADMIN,
               Task.USER_TYPE.SYSTEM_ADMIN
            )
        }, {
            label: language.translate('DELETE'),
            icon: "delete",
            color: "warn",
            onClick: async (usr) => {
                modal.open(ConfirmDialogComponent, async (succ) => {
                    if (succ) {
                        await User.remove(usr);
                        this.table.getData();
                    }
                }, {
                    title: language.translate("USER.DELETE.TITLE"),
                    message: language.translate("USER.DELETE.MSG", usr.first_name, usr.last_name),
                    icon: "person",
                    iconColor: color.variable(color.Variable.warn)
                });
            },
            priority: 3,
            isVisible: async (user) => await User.currentTypeIs(
               Task.USER_TYPE.HR_MANAGER,
               Task.USER_TYPE.ADMIN,
               Task.USER_TYPE.SYSTEM_ADMIN
            )
        },
      ]
   }
}

type DisplayUser = {
   status: template.EscapedHTML,
   name: template.EscapedHTML,
   is_student: template.EscapedHTML,
   institution: template.EscapedHTML,
   contact: template.EscapedHTML,
}